import {
  ACCOUNT_ACTIONS,
  ACCOUNT_AUTH_LEVELS,
  accountAuthLevel,
  isAccountActionImplemented,
  isKnownAccountAction
} from '@/constants/AccountConstants.ts'

const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const [{ useCustomerStore }, { useFeaturesStore }] = await Promise.all([
      import('@/stores/customer'),
      import('@/stores/features')
    ])
    // Getters, not resolved instances: a cached store instance belongs to
    // whichever pinia was active on first use, which on the server is another
    // request's app (VIP-12037).
    cache = {
      get customer() {
        return useCustomerStore()
      },
      get features() {
        return useFeaturesStore()
      }
    }
    return cache
  }
})()

/**
 * Login target for a route the customer may not see, exported because the session can also
 * turn out to be gone mid-navigation, after the guard already let them in (VIP-12080).
 */
const loginUrl = (_t) => {
  // The target route carries the same query as the address bar, and unlike
  // `window.location` it also exists during a server render (VIP-12037)
  const redirectParam = _t?.query?.redirect ?? null

  // Both branches must encode: an account deep link can carry its own query
  // (the notifications email+hash entry does, VIP-12082), and an unencoded `&`
  // ends up as a param of the login URL instead of the return target, silently
  // truncating it.
  return `/auth/v2/login?redirect_uri=${encodeURIComponent(
    redirectParam ?? _t?.fullPath ?? '/'
  )}`
}

const customerGuardV2 = async (_t, _f, next) => {
  const { customer } = await _getModules()

  await customer.waitUntilPending()

  if (!customer.customerSoftLoggedIn) {
    next(loginUrl(_t))
    return
  }

  next()
}

const adminGuard = async (_t, _f, next) => {
  const { customer } = await _getModules()

  await customer.waitUntilPending()

  // Nobody logged in is not the same as forbidden: send them through login and
  // back, and keep the error page for a signed-in non-admin.
  if (!customer.customerSoftLoggedIn) {
    next(loginUrl(_t))
    return
  }

  if (customer.customerIsAdmin) {
    next()
    return
  }

  return next({ name: 'error' })
}

/**
 * Guard for the v2 account routes. Mirrors the legacy per-tab semantics:
 * anonymous visitors go to login, and the tabs whose legacy controller calls
 * `forceVerifiedLogin()` send a soft-logged-in customer through the full login
 * with a `redirect_uri` back to the tab.
 *
 * Tabs that are not built yet show their placeholder to admins only and send
 * customers to profile, so replacing `adminGuard` here does not expose
 * placeholders (VIP-12097). Who reaches the v2 account area at all is the
 * monolith's `account_v2_redirect` flag (VIP-12098).
 */
const accountGuardV2 = async (_t, _f, next) => {
  const { customer } = await _getModules()

  await customer.waitUntilPending()

  if (!customer.customerSoftLoggedIn) {
    next(loginUrl(_t))
    return
  }

  // `/account/manage/v2` and unknown actions both land on profile via
  // TheAccountTemplate, so gate them like profile rather than erroring out.
  // Own-property check: indexing the map directly also answered for every
  // Object.prototype member, so `/account/manage/v2/constructor` counted as its
  // own known action and passed both gates below (VIP-12098).
  const action = isKnownAccountAction(_t?.params?.action)
    ? _t.params.action
    : ACCOUNT_ACTIONS.PROFILE

  // A tab with no component yet. Admins keep the placeholder preview; customers
  // go to profile, which is always built.
  //
  // Deliberately not a bounce to the legacy page, tempting as that is: the
  // monolith redirects the tabs it has mapped back here, so a bounce would loop
  // the day the two repos disagree about what is built. Landing on profile is
  // the wrong tab; looping is an unusable account area (VIP-12098).
  if (!isAccountActionImplemented(action) && !customer.customerIsAdmin) {
    next({
      name: 'account_action',
      params: { action: ACCOUNT_ACTIONS.PROFILE }
    })
    return
  }

  if (
    accountAuthLevel(action) === ACCOUNT_AUTH_LEVELS.VERIFIED &&
    !customer.customerActive
  ) {
    next(loginUrl(_t))
    return
  }

  next()
}

const ACCOUNT_ROUTE_NAMES = new Set(['account_default', 'account_action'])

/**
 * `beforeEnter` runs when a route *record* is entered, and every account tab
 * shares one record (`/account/manage/v2/:action/:id?`). A tab-to-tab
 * navigation therefore skips `accountGuardV2` completely, which would let a
 * customer already on an implemented tab reach an unmigrated one, or a
 * soft-logged-in customer cross from a plain-login tab into a verified one
 * without re-authenticating.
 *
 * This covers exactly that case — same record, params only — so it never
 * double-runs with the route's own `beforeEnter` (VIP-12097).
 */
const accountParamChangeGuard = (_t, _f, next) => {
  if (_t?.name === _f?.name && ACCOUNT_ROUTE_NAMES.has(_t?.name)) {
    return accountGuardV2(_t, _f, next)
  }

  next()
}

export {
  accountGuardV2,
  accountParamChangeGuard,
  adminGuard,
  customerGuardV2,
  loginUrl
}
